import { useEffect, useState } from 'react'
import { useAbp } from '../platform/abp/react'
import { preferredLoginAddress } from './user'
import {
  fetchImpersonationTargets,
  hasSavedImpersonationSession,
  impersonateUser,
  returnFromImpersonation,
  savedImpersonationReturnIdentity,
  type ImpersonationTarget,
} from './api/impersonation'

const STUDIO_ROLE_TITLES: [string, string][] = [
  ['scenariodeveloper', 'Developer'],
  ['scenarioadmin', 'Admin'],
  ['scenarioauthor', 'Author'],
  ['scenariotrainer', 'Trainer'],
  ['scenarioeditor', 'Editor'],
  ['scenarioparticipant', 'Participant'],
  ['scenariouser', 'User'],
]

function currentViewLabel(roleNames: readonly string[], email: string): string {
  const names = new Set(roleNames.map((name) => name.toLowerCase()))
  const title = STUDIO_ROLE_TITLES.find(([role]) => names.has(role))?.[1] ?? ''
  if (title && email) return `${title} (${email})`
  return email || title || 'Signed in'
}

export function ScenarioUserSwitcher() {
  const { can, isAuthenticated, bootstrap, roleNames } = useAbp()
  const [targets, setTargets] = useState<ImpersonationTarget[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const returning = hasSavedImpersonationSession()
  const isDeveloper = can('Buxus.Scenarios.Developer')
  const mayList = isAuthenticated && (returning || isDeveloper)

  useEffect(() => {
    if (!mayList) return
    let cancelled = false
    setTargets(null)
    void fetchImpersonationTargets()
      .then((rows) => {
        if (!cancelled) setTargets(rows)
      })
      .catch(() => {
        if (!cancelled) setTargets([])
      })
    return () => {
      cancelled = true
    }
  }, [mayList])

  if (!mayList || targets == null || targets.length === 0 || (!isDeveloper && !returning)) return null

  const identity = preferredLoginAddress(bootstrap?.email, bootstrap?.userName)
  const here = currentViewLabel(roleNames, identity)
  const selfLabel = identity ? `${identity} (Self)` : 'Self'
  const backTo = savedImpersonationReturnIdentity()
  const currentId = (bootstrap?.userId ?? '').toLowerCase()
  const currentTarget = targets.find((row) => row.userId.toLowerCase() === currentId)
  const selectValue = returning ? (currentTarget?.userId ?? 'current') : ''

  const onChange = (userId: string) => {
    if (!userId || userId === 'current' || userId.toLowerCase() === currentId || busy) return
    setBusy(true)
    setError('')
    void impersonateUser(userId).catch(() => {
      setBusy(false)
      setError('Could not switch user.')
    })
  }

  const onBack = () => {
    if (busy) return
    setBusy(true)
    setError('')
    void returnFromImpersonation().catch((err: unknown) => {
      setBusy(false)
      setError(err instanceof Error ? err.message : 'Could not return')
    })
  }

  return (
    <div className="sc-account-developer-tail">
      <div className="sc-field sc-debug sc-view-as">
        <span className="sc-flbl">Developer</span>
        <label className="sc-flbl" htmlFor="sc-view-as">
          Viewing as
        </label>
        <select
          id="sc-view-as"
          className="sc-fin"
          aria-label="Viewing as"
          title={here}
          disabled={busy}
          value={selectValue}
          onChange={(e) => onChange(e.target.value)}
        >
          {returning ? null : <option value="">{selfLabel}</option>}
          {returning && !currentTarget ? <option value="current">{here}</option> : null}
          {targets.map((row) => (
            <option key={row.userId} value={row.userId}>
              {row.label}
            </option>
          ))}
        </select>
        {returning ? (
          <button
            type="button"
            className="sc-btn sc-view-as-back"
            disabled={busy}
            title={backTo ? `Back to ${backTo}` : 'Back to your account'}
            onClick={onBack}
          >
            {backTo ? `Back to ${backTo}` : 'Back to your account'}
          </button>
        ) : null}
        {error ? <p className="sc-hint">{error}</p> : null}
      </div>
    </div>
  )
}
